/**
 * Home (Brief 185) — the activity-first landing. Metric cards (balance +
 * add-credit, active batons, spend this week) over the activity stream, with
 * actor filter chips (All · per-connected-agent · You). The transparent ledger
 * IS the home screen.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Plus, TrendingUp, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivityStream, type ActivityFilter } from '@/components/ActivityStream';
import { formatMicros } from '@/components/MoneyAmount';
import {
  accountKey,
  accountMetricsKey,
  connectionsListKey,
  fetchAccount,
  fetchAccountMetrics,
  fetchConnections,
} from '@/lib/queries';

function MetricCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: typeof Wallet;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function formatBytes(bytesStr: string): string {
  const n = Number(bytesStr);
  if (!Number.isFinite(n) || n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function Home() {
  const accountQ = useQuery({ queryKey: accountKey, queryFn: fetchAccount });
  const metricsQ = useQuery({ queryKey: accountMetricsKey, queryFn: fetchAccountMetrics });
  const connectionsQ = useQuery({ queryKey: connectionsListKey, queryFn: fetchConnections });

  // Actor filter chips: All · each connected agent · You.
  const filters: ActivityFilter[] = [{ key: 'all', label: 'All' }];
  const labelsByConn: Record<string, string> = {};
  for (const c of connectionsQ.data?.data ?? []) {
    const label = c.label ?? 'Connected agent';
    labelsByConn[c.id] = label;
    filters.push({ key: c.id, label, actor: c.id });
  }
  if (accountQ.data?.accountId) {
    filters.push({ key: 'you', label: 'You', actor: accountQ.data.accountId });
  }

  const m = metricsQ.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Home</h1>
        <p className="text-muted-foreground">
          Your balance, your agents, and everything they've done — newest first.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <MetricCard title="Balance" icon={Wallet}>
          {accountQ.isPending ? (
            <Skeleton className="h-7 w-24" />
          ) : (
            <>
              <div className="text-2xl font-semibold">
                {accountQ.data?.availableFormatted ??
                  accountQ.data?.balanceFormatted ??
                  formatMicros(accountQ.data?.balanceMicros ?? '0')}
              </div>
              <Button asChild size="sm" variant="outline" className="mt-3 gap-1">
                <Link to="/dashboard/billing">
                  <Plus className="h-3.5 w-3.5" />
                  Add credit
                </Link>
              </Button>
            </>
          )}
        </MetricCard>

        <MetricCard title="Active batons" icon={Boxes}>
          {metricsQ.isPending ? (
            <Skeleton className="h-7 w-16" />
          ) : (
            <>
              <div className="text-2xl font-semibold">{m?.batons.active ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {formatBytes(m?.batons.storageBytes ?? '0')} stored
                {m?.batons.nextExpiry
                  ? ` · next expiry ${new Date(m.batons.nextExpiry).toLocaleDateString()}`
                  : ''}
              </p>
            </>
          )}
        </MetricCard>

        <MetricCard title="Spend this week" icon={TrendingUp}>
          {metricsQ.isPending ? (
            <Skeleton className="h-7 w-20" />
          ) : (
            <>
              <div className="text-2xl font-semibold">
                {formatMicros(m?.spend.spentMicros ?? '0')}
              </div>
              <p className="text-xs text-muted-foreground">
                {m?.spend.calls ?? 0} calls · {m?.spend.agents ?? 0} agents/keys ·{' '}
                {m?.spend.windowDays ?? 7}d
              </p>
            </>
          )}
        </MetricCard>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <ActivityStream
            filters={filters}
            labels={{
              connections: labelsByConn,
              selfId: accountQ.data?.accountId ?? null,
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
